import React, { useCallback, useEffect, useState } from 'react';
import { Button, Tooltip, Typography } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import Drawer from 'components/Drawer';
import FilterForm, { getFilter } from 'components/GlobalFilter/FilterForm';
import QueryBuilderInput from 'components/QueryBuilder/QueryBuilderInput';
import { useMutation } from 'hooks/apollo';
import { useResultFilterSummary } from './useResultFilterSummary';

const CREATE_GLOBAL_FILTER_MUTATION = gql`
  mutation CreateGlobalFilter($customerId: ID!, $dashboardContentId: ID, $input: GlobalFilterInput!) {
    createGlobalFilter(customerId: $customerId, dashboardContentId: $dashboardContentId, input: $input) {
      id
    }
  }
`;

const UPDATE_GLOBAL_FILTER_MUTATION = gql`
  mutation UpdateGlobalFilter($id: ID!, $input: GlobalFilterInput!) {
    updateGlobalFilter(id: $id, input: $input) {
      id
    }
  }
`;

const DELETE_GLOBAL_FILTER_MUTATION = gql`
  mutation DeleteGlobalFilter($id: ID!) {
    deleteGlobalFilter(id: $id) {
      id
    }
  }
`;

const ResultFilter = ({
  queryFilter,
  scopeFilter,
  runtimeQueryFilter,
  globalFilter,
  customerId,
  contentId,
  websites,
  canEditQuery,
  canEditScope,
  onQueryFilterSave,
  onScopeFilterChange,
  onRemoveScopeFilter,
}) => {
  const [open, setOpen] = useState(false);
  const [draftQueryFilter, setDraftQueryFilter] = useState(queryFilter);
  const [scopeFilterDraft, setScopeFilterDraft] = useState(null);
  const [createGlobalFilter] = useMutation(CREATE_GLOBAL_FILTER_MUTATION);
  const [updateGlobalFilter] = useMutation(UPDATE_GLOBAL_FILTER_MUTATION);
  const [deleteGlobalFilter] = useMutation(DELETE_GLOBAL_FILTER_MUTATION);

  const { summary, hasFilter } = useResultFilterSummary({
    queryFilter,
    scopeFilter,
    runtimeQueryFilter,
    customerId,
  });

  const canEdit = canEditQuery || canEditScope;

  useEffect(() => {
    setDraftQueryFilter(queryFilter);
  }, [queryFilter]);

  const openDrawer = useCallback(() => {
    setDraftQueryFilter(queryFilter);
    setScopeFilterDraft(scopeFilter || getFilter(globalFilter));
    setOpen(true);
  }, [queryFilter, scopeFilter, globalFilter]);

  const onQueryFilterChanged = useCallback(async (filter) => {
    setDraftQueryFilter(filter);
    if (canEditQuery) {
      await onQueryFilterSave?.(filter);
    }
  }, [canEditQuery, onQueryFilterSave]);

  const onScopeFilterChanged = useCallback(async (values) => {
    const base = scopeFilterDraft || scopeFilter || getFilter(globalFilter);

    if (!base.id) {
      await createGlobalFilter({
        variables: {
          customerId,
          dashboardContentId: contentId,
          input: { ...getFilter(base), ...values },
        },
      });
    } else {
      await updateGlobalFilter({
        variables: {
          id: base.id,
          input: values,
        },
      });
    }
    onScopeFilterChange?.();
  }, [scopeFilterDraft, scopeFilter, globalFilter, customerId, contentId, createGlobalFilter, updateGlobalFilter, onScopeFilterChange]);

  const onRemoveScope = useCallback(async () => {
    const filterId = scopeFilter?.id || scopeFilterDraft?.id;
    if (filterId) {
      await deleteGlobalFilter({ variables: { id: filterId } });
    }
    setScopeFilterDraft(null);
    setOpen(false);
    onRemoveScopeFilter?.();
  }, [scopeFilter, scopeFilterDraft, deleteGlobalFilter, onRemoveScopeFilter]);

  if (!canEdit && !hasFilter) {
    return null;
  }

  const button = (
    <Button
      icon={<FilterOutlined />}
      type={hasFilter ? 'primary' : 'default'}
      onClick={canEdit ? openDrawer : undefined}
      aria-label="Result filter"
    />
  );

  return (
    <>
      {summary ? <Tooltip title={summary}>{button}</Tooltip> : button}
      {canEdit && (
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          title="Result filter"
          onRemove={canEditScope && (scopeFilter?.id || scopeFilterDraft?.id) ? onRemoveScope : undefined}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {canEditQuery && (
              <div>
                <Typography.Paragraph type="secondary">
                  Additional query constraints applied on top of the global filter.
                </Typography.Paragraph>
                <QueryBuilderInput
                  customerId={customerId}
                  value={draftQueryFilter}
                  onChange={onQueryFilterChanged}
                />
              </div>
            )}
            {canEditScope && (
              <div>
                {canEditQuery && (
                  <Typography.Paragraph type="secondary">
                    Replace the global filter entirely for this result (date range, segments, data sources).
                  </Typography.Paragraph>
                )}
                <FilterForm
                  onChange={onScopeFilterChanged}
                  filter={scopeFilterDraft}
                  websites={websites}
                />
              </div>
            )}
          </div>
        </Drawer>
      )}
    </>
  );
};

export default ResultFilter;
